import { ConfirmationGuard } from "../../../../components/ConfirmationGuard";
import { DesignLogo } from "../../../../../core/models/entities/Design";
import DigitalColorBlock from "../../../logo-editor/componenets/DigitalColorBlock";
import { StrucRow } from "../../../../components/StructureElements/StrucRow";
import { StrucCol } from "../../../../components/StructureElements/StrucCol";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClone,
  faEdit,
  faFile,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { BinaryStatusGoodBad } from "./binary-status-good-bad.component";
import IconButton from "../../../../components/LogoButton/IconButton";
import {
  BaseLogo,
  ImageLogo,
  isDigital,
  LogoEditStatus,
  LogoType,
  getTransferDisplayName,
} from "../../../../../core/models/entities/BaseLogo";
import { getColorsElements } from "../../../order-create/subcomponents/ProductDetailsLogo";
import { CloneIcon } from "../../../../../resources/icons/Icons";
import { LogoImage } from "../../../../components/Logo/LogoImage";
import { LogoDimensions } from "../../../../components/Logo/LogoDimensions";
import { shouldAllowCloning } from "../../../../shared/functions/shouldAllowCloning";

interface LogoInDesignCard_VM {
  logo: DesignLogo;
  placement?: boolean;
  disable?: boolean;
  isActive: boolean;
  onRemove: () => void;
  onAddCopy: () => void;
  onEdit: () => void;
  onClone: () => void;
}
export function LogoInDesignCard({
  logo,
  placement,
  disable,
  isActive,
  onEdit,
  onRemove,
  onAddCopy,
  onClone,
}: LogoInDesignCard_VM) {
  const isRaster = isDigital(logo.logo);
  const colors = getColorsElements(logo.logo);
  const color = isRaster ? <DigitalColorBlock /> : colors;

  const addCopyButton = (
    <IconButton
      width={"full"}
      icon={<FontAwesomeIcon icon={faClone} />}
      onClick={onAddCopy}
    >
      Add Copy
    </IconButton>
  );

  const removeButton = (
    <IconButton
      width={"full"}
      icon={<FontAwesomeIcon icon={faTrash} />}
      onClick={onRemove}
    >
      Remove
    </IconButton>
  );

  function actions(logo: BaseLogo) {
    switch (logo.logoType) {
      case LogoType.Unknown:
      case LogoType.TextLogo:
      case LogoType.NameLogo:
      case LogoType.NumberLogo:
      case LogoType.SymbolLogo:
        return (
          <>
            {addCopyButton}
            {removeButton}
          </>
        );
      case LogoType.ImageLogo: {
        const transferType = logo.transferMethod.type.toLowerCase();
        const isEmbroidery =
          transferType === "broderi" || transferType === "embroidery";
        const imageLogo = logo as ImageLogo;
        const canClone = shouldAllowCloning(logo);

        return (
          <>
            {!isEmbroidery &&
              (imageLogo.status === LogoEditStatus.Draft ? (
                <IconButton
                  width={"full"}
                  key={"edit"}
                  icon={<FontAwesomeIcon icon={faEdit} />}
                  onClick={onEdit}
                >
                  Edit
                </IconButton>
              ) : (
                canClone && (
                  <ConfirmationGuard
                    anchor={
                      <IconButton
                        width={"full"}
                        icon={<FontAwesomeIcon icon={faClone} />}
                      >
                        Clone Logo
                      </IconButton>
                    }
                    text={
                      "Are you sure?\n\nThis action will create a new logo on a new item number."
                    }
                    onAccept={onClone}
                  />
                )
              ))}
            {addCopyButton}
            {removeButton}
          </>
        );
      }
    }
  }

  const buttons = actions(logo.logo);

  return (
    <div
      className={`border rounded bg-gray-100 ${
        isActive ? "border-gray-600" : ""
      } ${disable ? "opacity-50" : ""}`}
    >
      <StrucRow className={"pl-1 pr-2 py-1 border-b"}>
        <StrucCol className={"!justify-between !flex-row items-center"}>
          <div className={"text-[#1B76DF] flex items-center space-x-2"}>
            <FontAwesomeIcon
              className={"text-xs px-1.5 py-1 rounded border"}
              icon={faFile}
            />
            <span
              className={
                "max-w-[200px] overflow-x-hidden overflow-ellipsis whitespace-nowrap"
              }
            >
              {logo.logo.title}
            </span>
          </div>
        </StrucCol>
        {placement !== undefined && (
          <StrucCol className={"!flex-row !justify-end"} size={3.5}>
            <BinaryStatusGoodBad
              verdict={placement}
              goodOption={"Valid Placement"}
              badOption={"Invalid Placement"}
            />
          </StrucCol>
        )}
      </StrucRow>
      <StrucRow>
        <StrucCol size={3.5} className={"bg-white p-3"}>
          <div className={"h-screen flex items-center max-h-[55px] w-fit"}>
            {logo.alternativeTextImageUri ? (
              <img
                src={logo.alternativeTextImageUri}
                alt={logo.logo.title}
                className={
                  "bg-white hover:rounded hover:border hover:p-2 max-h-[74px] origin-left hover:scale-[2] hover:ease-out transition-all duration-150 ease-out"
                }
              />
            ) : (
              <LogoImage
                logo={logo.logo}
                useHighRes={true}
                scaleNameByHeight
                className={
                  "bg-white hover:rounded hover:border hover:p-2 max-h-[74px] origin-left hover:scale-[2] hover:ease-out transition-all duration-150 ease-out"
                }
              />
            )}
          </div>
        </StrucCol>
        <StrucCol size={5} className={"pl-2 py-2 text-xs space-y-1"}>
          {logo.logo.product?.sku &&
          !logo.logo.product.sku.startsWith("UPLOAD_") ? (
            <span>
              <b>Item No.</b> {logo.logo.product.sku}
            </span>
          ) : (
            ""
          )}
          <span className="capitalize">
            <b>Type</b>{" "}
            {logo.logo.transferMethod.type.toLowerCase() === "direk tryk" ||
            logo.logo.transferMethod.type.toLowerCase() === "screen print"
              ? "Screen Print"
              : logo.logo.transferMethod.type.toLowerCase() === "broderi" ||
                  logo.logo.transferMethod.type.toLowerCase() === "embroidery"
                ? "Embroidery"
                : logo.logo.transferMethod.type.toLowerCase()}
          </span>
          {logo.logo.transferMethod.type.toLowerCase() !== "direk tryk" &&
            logo.logo.transferMethod.type.toLowerCase() !== "screen print" &&
            logo.logo.transferMethod.type.toLowerCase() !== "broderi" &&
            logo.logo.transferMethod.type.toLowerCase() !== "embroidery" && (
              <span className="capitalize">
                <b>Method</b> {getTransferDisplayName(logo.logo).toLowerCase()}
              </span>
            )}
          <div>
            <b>Size</b> <LogoDimensions logo={logo.logo} format="WWxYYmm" />
          </div>
          <div className="flex gap-x-1">
            <span className={"capitalize font-bold"}>Colors</span>
            <div>{color}</div>
          </div>
        </StrucCol>
        <StrucCol className={"pr-2 pt-2 space-y-2"} size={3.5}>
          {buttons}
        </StrucCol>
      </StrucRow>
    </div>
  );
}
